import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { mapFilterParams } from '../utils/filterMapping.js';

// Initial filter state based on story requirements
const FILTER_STATE_VERSION = 3; // Increment this when changing defaults that need migration

const initialFilterState = {
  version: FILTER_STATE_VERSION,
  mineralType: 'all', // 'all' | 'critical' | 'non-critical'
  specificCommodities: [], // Selected commodities array
  countries: [], // Selected countries array
  mineTypes: [], // Selected mine types array
  mineStatus: ['Active', 'Development', 'Suspended'], // Include suspended for MVP display
  includeArtisanal: false, // Artisanal mines hidden by default
  viewport: {
    bounds: null,
    zoom: null
  }
};

// Create Zustand FilterStore with persist middleware
export const useFilterStore = create(
  persist(
    (set, get) => ({
      // State
      ...initialFilterState,

      // Actions
      getFilters: () => {
        const state = get();
        return {
          mineralType: state.mineralType,
          specificCommodities: state.specificCommodities,
          countries: state.countries,
          mineTypes: state.mineTypes,
          mineStatus: state.mineStatus,
          includeArtisanal: state.includeArtisanal,
        };
      },

      setFilter: (key, value) => {
        set((state) => ({
          ...state,
          [key]: value
        }));
      },

      resetFilters: () => {
        set(() => ({
          ...initialFilterState,
          viewport: get().viewport // Preserve viewport state during reset
        }));

        // Also clear localStorage cache to prevent filter persistence issues
        try {
          localStorage.removeItem('mining-filters');
          console.log('FilterStore: Cleared localStorage cache during reset');
        } catch (error) {
          console.warn('FilterStore: Failed to clear localStorage cache:', error);
        }
      },

      // Update viewport separately from filters
      setViewport: (bounds, zoom) => {
        set((state) => ({
          ...state,
          viewport: { bounds, zoom }
        }));
      },

      // Get API parameters formatted for backend consumption
      getAPIParams: () => {
        const state = get();
        const mappedFilters = mapFilterParams(state);

        // Convert array values to comma-separated strings for Axios
        // The backend expects arrays as comma-separated strings, not bracket notation
        const serializedFilters = {};
        for (const [key, value] of Object.entries(mappedFilters)) {
          if (Array.isArray(value)) {
            serializedFilters[key] = value.join(',');
          } else {
            serializedFilters[key] = value;
          }
        }

        return {
          bounds: state.viewport.bounds,
          zoom: state.viewport.zoom,
          filters: serializedFilters
        };
      },

      // URL synchronization methods
      getURLParams: () => {
        const state = get();
        const params = new URLSearchParams();
        
        // Add non-default filter values to URL
        if (state.mineralType !== 'all') {
          params.set('mineralType', state.mineralType);
        }
        
        if (state.specificCommodities.length > 0) {
          params.set('commodities', state.specificCommodities.join(','));
        }
        
        if (state.countries.length > 0) {
          params.set('countries', state.countries.join(','));
        }
        
        if (state.mineTypes.length > 0) {
          params.set('mineTypes', state.mineTypes.join(','));
        }
        
        // Only add status if different from default
        const defaultStatus = ['Active', 'Development', 'Suspended'];
        const statusDifferent = state.mineStatus.length !== defaultStatus.length ||
          !state.mineStatus.every(status => defaultStatus.includes(status));
        
        if (statusDifferent) {
          params.set('status', state.mineStatus.join(','));
        }

        if (state.includeArtisanal) {
          params.set('artisanal', 'true');
        }

        return params;
      },

      loadFromURL: () => {
        const urlParams = new URLSearchParams(window.location.search);
        const updates = {};
        
        // Parse mineral type
        const mineralType = urlParams.get('mineralType');
        if (mineralType && ['all', 'critical', 'non-critical'].includes(mineralType)) {
          updates.mineralType = mineralType;
        }
        
        // Parse array parameters
        const commodities = urlParams.get('commodities');
        if (commodities) {
          updates.specificCommodities = commodities.split(',').filter(Boolean);
        }
        
        const countries = urlParams.get('countries');
        if (countries) {
          updates.countries = countries.split(',').filter(Boolean);
        }
        
        const mineTypes = urlParams.get('mineTypes');
        if (mineTypes) {
          updates.mineTypes = mineTypes.split(',').filter(Boolean);
        }
        
        const status = urlParams.get('status');
        if (status) {
          updates.mineStatus = status.split(',').filter(Boolean);
        }

        const artisanal = urlParams.get('artisanal');
        if (artisanal === 'true') {
          updates.includeArtisanal = true;
        }

        // Apply all updates at once
        if (Object.keys(updates).length > 0) {
          set((state) => ({ ...state, ...updates }));
          console.log('FilterStore: Loaded filters from URL:', updates);
        }
      },

      updateURL: () => {
        const { getURLParams } = get();
        const params = getURLParams();
        
        // Update URL without page reload
        const newURL = params.toString() ? 
          `${window.location.pathname}?${params.toString()}` : 
          window.location.pathname;
          
        window.history.replaceState(null, '', newURL);
        console.log('FilterStore: Updated URL with current filters');
      }
    }),
    {
      name: 'mining-filters', // localStorage key
      version: FILTER_STATE_VERSION,
      // Persist all filter state but exclude viewport for performance
      partialize: (state) => ({
        version: state.version,
        mineralType: state.mineralType,
        specificCommodities: state.specificCommodities,
        countries: state.countries,
        mineTypes: state.mineTypes,
        mineStatus: state.mineStatus,
        includeArtisanal: state.includeArtisanal
      }),
      // Migrate old cached state when version changes
      migrate: (persistedState, version) => {
        const state = persistedState;

        // Migration from version 1 (or undefined) to version 2
        // Version 2 adds 'Suspended' to default mineStatus
        if (!state.version || state.version < 2) {
          console.log('FilterStore: Migrating from version', state.version || 1, 'to version 2');

          // If mineStatus is the old default ['Active', 'Development'], update to include 'Suspended'
          if (
            state.mineStatus &&
            state.mineStatus.length === 2 &&
            state.mineStatus.includes('Active') &&
            state.mineStatus.includes('Development') &&
            !state.mineStatus.includes('Suspended')
          ) {
            state.mineStatus = ['Active', 'Development', 'Suspended'];
            console.log('FilterStore: Updated mineStatus to include Suspended');
          }

          state.version = 2;
        }

        // Migration from version 2 to version 3
        // Version 3 adds includeArtisanal flag
        if (state.version < 3) {
          console.log('FilterStore: Migrating from version', state.version, 'to version 3');
          state.includeArtisanal = false;
          state.version = 3;
        }

        return state;
      }
    }
  )
);